import { useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { config } from './config'
import { Loading, useAuth } from './auth'

export function RequireUser({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <Loading />
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <Loading />
  if (!user || user.role !== 'admin') return <Navigate to="/admin/login" replace />
  return <>{children}</>
}

function LoginCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-white">
      <div className="w-full max-w-sm border border-[#E5E7EC] p-8 text-center" style={{ borderTop: '2px solid #92A2BD' }}>
        <img src="/bmw-mini-lockup-grey.png" alt="BMW + MINI" className="mx-auto mb-6 h-7" />
        {children}
      </div>
    </div>
  )
}

export function UserLoginPage() {
  const { user, loading, refresh } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (loading) return <Loading />
  if (user) return <Navigate to="/" replace />

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })
      if (!res.ok) {
        setError(res.status === 429 ? 'Too many attempts. Please wait a few minutes.' : 'Invalid credentials.')
        return
      }
      await refresh()
      navigate('/', { replace: true })
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <LoginCard>
      <h1 className="mb-1 text-[15px] font-bold uppercase tracking-[0.08em] text-black">Sign in</h1>
      <p className="mb-6 text-xs text-[#666]">BMW Stock Order Configurator</p>
      {error && <p className="mb-4 border border-black bg-bmw-paper px-3 py-2 text-xs text-black">{error}</p>}
      <form onSubmit={onSubmit} className="space-y-3 text-left">
        <input type="text" autoComplete="username" placeholder="Username" value={username}
          onChange={e => setUsername(e.target.value)} required
          className="w-full border border-[#E5E7EC] px-3 py-2.5 text-xs text-black focus:border-bmw-ocean focus:outline-none" />
        <input type="password" autoComplete="current-password" placeholder="Password" value={password}
          onChange={e => setPassword(e.target.value)} required
          className="w-full border border-[#E5E7EC] px-3 py-2.5 text-xs text-black focus:border-bmw-ocean focus:outline-none" />
        <button type="submit" disabled={submitting}
          className="w-full bg-bmw-ocean px-4 py-2.5 text-xs font-bold uppercase tracking-[0.06em] text-white hover:opacity-90 disabled:opacity-50">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </LoginCard>
  )
}

export function AdminLoginPage() {
  const { user, loading } = useAuth()
  const [params] = useSearchParams()
  const error = params.get('error')
  if (loading) return <Loading />
  if (user && user.role === 'admin') return <Navigate to="/admin" replace />
  return (
    <LoginCard>
      <h1 className="mb-1 text-[15px] font-bold uppercase tracking-[0.08em] text-black">Admin Sign-in</h1>
      <p className="mb-6 text-xs text-[#666]">Restricted to Solvigo administrators.</p>
      {error && (
        <p className="mb-4 border border-black bg-bmw-paper px-3 py-2 text-xs text-black">
          {error === 'not_authorized' ? 'That account is not authorized for admin access.' : 'Sign-in failed. Please try again.'}
        </p>
      )}
      <a href={`${config.apiUrl}/api/auth/google`}
        className="inline-block w-full bg-bmw-ocean px-4 py-2.5 text-xs font-bold uppercase tracking-[0.06em] text-white hover:opacity-90">
        Sign in with Google
      </a>
    </LoginCard>
  )
}
